import type { ReactElement } from 'react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { ChevronDown, ChevronRight } from 'lucide-react';
import type { AuditLog } from '@kirin/api-client/contracts/audit-logs-table';
import {
  readAuditActorKeyRef,
  readAuditApiKeyRef,
} from '@kirin/api-client/contracts/audit-logs';
import { ApiKeySourceBadge, OrgKeySourceBadge } from './ApiKeySourceBadge';
import { OperationBadge } from './OperationBadge';
import { formatAuditUserDisplay } from './utils';

/** Raw `table_name`, shown as-is so it matches the dropdown option labels. */
export function ObjectTypeCell({
  tableName,
}: {
  tableName: string;
}): ReactElement {
  return <span className="text-sm">{tableName}</span>;
}

/**
 * Originating surface for policy edits, read from `metadata.source` recorded by
 * `audit_trigger_func` via the `app.audit_metadata` GUC (see migration 0110).
 * Renders "Alerts" / "Policies" as a small badge; dash when no source is
 * recorded (login/logout, non-policy tables, or pre-KIR-3550 rows).
 */
export function AuditSourceCell({
  metadata,
}: {
  metadata: Record<string, unknown> | null;
}): ReactElement {
  const rawSource = metadata?.source;
  const source = typeof rawSource === 'string' ? rawSource : null;
  if (source !== 'alerts' && source !== 'policies') {
    return <span className="text-muted-foreground text-sm">-</span>;
  }
  const label = source === 'alerts' ? 'Alerts' : 'Policies';
  return (
    <span className="inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-medium text-muted-foreground">
      {label}
    </span>
  );
}

/** Colored outline badge for the operation. */
export function OperationBadgeCell({
  operation,
}: {
  operation: AuditLog['operation'];
}): ReactElement {
  return <OperationBadge operation={operation} />;
}

/** Full monospace record id, wrapping within the column; `-` when null. */
export function ObjectIdCell({
  recordId,
}: {
  recordId: string | null;
}): ReactElement {
  if (!recordId) {
    return <span className="text-muted-foreground">-</span>;
  }

  return (
    <span className="font-mono break-all text-muted-foreground">
      {recordId}
    </span>
  );
}

/**
 * User display as name → email → "System"; the raw id is never shown.
 *
 * When the row came from a management API key (KIR-3751) the name is followed by
 * an API badge. The person named here minted that key and stays accountable —
 * the badge disambiguates the mechanism, it does not reassign the actor.
 *
 * A user-less row written with an org-level MDM or plugin key shows "System"
 * with an "MDM key" / "Plugin key" badge instead.
 *
 * The badge sits *outside* the name's tooltip trigger on purpose: the trigger is
 * the name alone, so the email tooltip and the badge's own key tooltip stay
 * independent instead of competing for the same hover target.
 */
export function AuditLogUserCell({
  userName,
  userEmail,
  metadata,
}: {
  userName: string | null;
  userEmail: string | null;
  metadata?: Record<string, unknown> | null;
}): ReactElement {
  const display = formatAuditUserDisplay(userName, userEmail);
  const apiKey = readAuditApiKeyRef(metadata);
  const actorKey = apiKey === null ? readAuditActorKeyRef(metadata) : null;

  const name =
    userName && userEmail ? (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="cursor-help text-sm">{display}</span>
          </TooltipTrigger>
          <TooltipContent>{userEmail}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    ) : (
      <span className="text-sm">{display}</span>
    );

  let badge: ReactElement | null = null;
  if (apiKey !== null) {
    badge = <ApiKeySourceBadge apiKey={apiKey} />;
  } else if (actorKey !== null) {
    badge = <OrgKeySourceBadge actorKey={actorKey} />;
  }
  if (badge === null) {
    return name;
  }

  return (
    <span className="flex min-w-0 flex-wrap items-center gap-1.5">
      {name}
      {badge}
    </span>
  );
}

/** Collapsible from→to diff of changed fields; "No changes" when empty. */
export function ChangedFieldsDisplay({
  changedFields,
}: {
  changedFields: Record<string, unknown> | null;
}): ReactElement {
  const [isOpen, setIsOpen] = useState(false);

  if (!changedFields || Object.keys(changedFields).length === 0) {
    return <span className="text-muted-foreground text-sm">No changes</span>;
  }

  const fieldCount = Object.keys(changedFields).length;

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <CollapsibleTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="flex items-center gap-1 h-auto p-1 max-w-full overflow-hidden"
        >
          {isOpen ? (
            <ChevronDown className="h-4 w-4 flex-shrink-0" />
          ) : (
            <ChevronRight className="h-4 w-4 flex-shrink-0" />
          )}
          <span className="text-sm truncate">
            {fieldCount} field{fieldCount !== 1 ? 's' : ''} changed
          </span>
        </Button>
      </CollapsibleTrigger>
      <CollapsibleContent className="mt-2">
        <div className="max-h-64 overflow-y-auto overflow-x-auto rounded border bg-muted/30 p-2">
          <div className="space-y-2 text-sm">
            {Object.entries(changedFields).map(([field, change]) => {
              const changeValue = change as { from: unknown; to: unknown };
              return (
                <div
                  key={field}
                  className="border-l-2 border-muted-foreground/30 pl-3"
                >
                  <div className="font-medium break-words">{field}</div>
                  <div className="text-muted-foreground break-words">
                    <span className="line-through">
                      {changeValue.from !== null &&
                      changeValue.from !== undefined
                        ? JSON.stringify(changeValue.from)
                        : 'null'}
                    </span>
                    {' → '}
                    <span className="font-medium">
                      {changeValue.to !== null && changeValue.to !== undefined
                        ? JSON.stringify(changeValue.to)
                        : 'null'}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}
